import type { FastifyInstance, RouteShorthandOptions } from 'fastify'
import type {
  CreatePlanDTO,
  UpdatePlanDTO,
  CreateStageDTO,
  UpdateStageDTO,
  GeneratePlanDTO,
  SharePlanDTO,
  UpdateSyncStateDTO,
  PlannerPreferences
} from '@my-roadtrip/types'
import {
  createPlanSchema,
  updatePlanSchema,
  createStageSchema,
  updateStageSchema,
  generatePlanSchema,
  sharePlanSchema,
  updateSyncStateSchema,
  plannerPreferencesSchema,
  reorderStagesSchema
} from '@my-roadtrip/validators'
import { PlannerController } from './planner.controller'
import { authMiddleware } from '../../middleware/auth.middleware'
import { validateRequest } from '../../middleware/validate.middleware'


export async function plannerRoutes(
  fastify: FastifyInstance,
  controller: PlannerController
) {
  const minute = '1 minute'
  const rateConfig = (max: number, timeWindow: string = minute): RouteShorthandOptions => ({
    config: {
      rateLimit: { max, timeWindow }
    }
  })

  // Public route (NO auth required)
  fastify.get('/plans/public', controller.getPublicPlans.bind(controller))

  // Apply auth to all other routes
  fastify.addHook('onRequest', authMiddleware)

  // Plans
  fastify.get(
    '/dashboard',
    rateConfig(30),
    controller.getDashboard.bind(controller)
  )

  fastify.get(
    '/preferences',
    rateConfig(60),
    controller.getPreferences.bind(controller)
  )

  fastify.patch<{ Body: Partial<PlannerPreferences> }>(
    '/preferences',
    {
      ...rateConfig(40),
      preHandler: validateRequest(plannerPreferencesSchema.partial())
    },
    controller.updatePreferences.bind(controller)
  )

  fastify.get('/plans', rateConfig(100), controller.getPlans.bind(controller))

  fastify.get(
    '/plans/current',
    rateConfig(100),
    controller.getCurrentPlan.bind(controller)
  )

  fastify.get<{ Params: { id: string } }>(
    '/plans/:id',
    rateConfig(100),
    controller.getPlan.bind(controller)
  )

  fastify.post<{ Body: CreatePlanDTO }>(
    '/plans',
    {
      ...rateConfig(20),
      preHandler: validateRequest(createPlanSchema)
    },
    controller.createPlan.bind(controller)
  )

  fastify.post<{ Body: GeneratePlanDTO }>(
    '/plans/generate',
    {
      ...rateConfig(10), // actual quota enforcement handled by billing service
      preHandler: validateRequest(generatePlanSchema)
    },
    controller.generatePlan.bind(controller)
  )

  fastify.patch<{ Params: { id: string }; Body: UpdatePlanDTO }>(
    '/plans/:id',
    {
      ...rateConfig(50),
      preHandler: validateRequest(updatePlanSchema)
    },
    controller.updatePlan.bind(controller)
  )

  fastify.delete<{ Params: { id: string } }>(
    '/plans/:id',
    rateConfig(20),
    controller.deletePlan.bind(controller)
  )

  // Stages
  fastify.post<{ Params: { planId: string }; Body: CreateStageDTO }>(
    '/plans/:planId/stages',
    {
      ...rateConfig(50),
      preHandler: validateRequest(createStageSchema)
    },
    controller.createStage.bind(controller)
  )

  fastify.patch<{ Params: { id: string }; Body: UpdateStageDTO }>(
    '/stages/:id',
    {
      ...rateConfig(50),
      preHandler: validateRequest(updateStageSchema)
    },
    controller.updateStage.bind(controller)
  )

  fastify.delete<{ Params: { id: string } }>(
    '/stages/:id',
    rateConfig(50),
    controller.deleteStage.bind(controller)
  )

  // Reorder stages
  fastify.patch<{ Params: { planId: string }; Body: { stageIds: string[] } }>(
    '/plans/:planId/stages/order',
    {
      ...rateConfig(50),
      preHandler: validateRequest(reorderStagesSchema)
    },
    controller.reorderStages.bind(controller)
  )

  // Route optimization
  fastify.post<{ Params: { planId: string } }>(
    '/plans/:planId/optimize',
    rateConfig(10),
    controller.optimizeRoute.bind(controller)
  )

  // Share plan
  fastify.post<{ Params: { id: string }; Body: SharePlanDTO }>(
    '/plans/:id/share',
    {
      ...rateConfig(20),
      preHandler: validateRequest(sharePlanSchema)
    },
    controller.sharePlan.bind(controller)
  )

  // CRDT Sync
  fastify.get<{ Params: { id: string } }>(
    '/plans/:id/sync',
    rateConfig(60),
    controller.getSyncState.bind(controller)
  )

  fastify.post<{ Params: { id: string }; Body: UpdateSyncStateDTO }>(
    '/plans/:id/sync',
    {
      ...rateConfig(60),
      preHandler: validateRequest(updateSyncStateSchema)
    },
    controller.updateSyncState.bind(controller)
  )
}
